import type { CustomFormApi } from 'informed';
import { Form } from 'informed';
import type { ChangeEvent } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { Button } from '@/components/buttons';
import { Input } from '@/components/form/Input';
import { ThemeMode } from '@/helpers/enums';
import { required } from '@/helpers/validators';
import { Intercom } from '@/hooks/3dParty/intercom';
import { useUserCanIssueBonuses } from '@/hooks/bonuses';
import { sendPromoNDBCodeAutofillEvent } from '@/modules/googleAnalytics/events/ndbEvents';
import {
  promocodeNormalizer,
  scrollToPromoCodeInput,
} from '@/modules/promotions/helpers/promocode';
import { usePromocodeActivation } from '@/modules/promotions/hooks';
import { useGetCommonStore } from '@/store';
import { useDeleteUserStorageItems } from '@/store/serverUserStorage';
import { SERVER_USER_STORAGE_IDENTIFIER } from '@/store/serverUserStorage/helpers';
import { DISTANCE_TO_TOP } from '../../helpers';
import { ScInputComponent } from './styled';

interface IInputComponentProps {
  onPromocodeActivationSuccess: () => void;
  ndbCoupon?: string;
}

export interface IPromocodeForm {
  promocode: string;
}

export const InputPromo = memo(
  ({ onPromocodeActivationSuccess, ndbCoupon }: IInputComponentProps) => {
    const { t } = useTranslation(['bonuses', 'promotions']);
    const { blackColor } = useTheme();
    const ref = useRef<HTMLDivElement | null>(null);
    const formApiRef = useRef<CustomFormApi<IPromocodeForm>>();

    const useCommonStore = useGetCommonStore();
    const isMobileDevice = useCommonStore.use.isMobileDevice();

    const [isError, setIsError] = useState(false);
    const [isErrorSupportClicked, setIsErrorSupportClicked] = useState(false);
    const [promocodePending, setPromocodePending] = useState(false);

    const isUserCanIssueBonuses = useUserCanIssueBonuses();

    const { handleSubmit } = usePromocodeActivation({
      setPromocodePending,
      onPromocodeActivationSuccess,
      setIsError,
      setIsErrorSupportClicked,
      formApiRef,
    });

    const { deleteUserStorageItems } = useDeleteUserStorageItems();
    const hasDivergedFromPrefilledRef = useRef(false);
    const firedForCouponRef = useRef<string | undefined>(undefined);

    useEffect(() => {
      hasDivergedFromPrefilledRef.current = false;
    }, [ndbCoupon]);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        setIsError(false);
        formApiRef.current?.setError('promocode', undefined);

        if (
          hasDivergedFromPrefilledRef.current ||
          !ndbCoupon ||
          promocodeNormalizer(e.target.value) === promocodeNormalizer(ndbCoupon)
        ) {
          return;
        }

        hasDivergedFromPrefilledRef.current = true;
        deleteUserStorageItems([
          SERVER_USER_STORAGE_IDENTIFIER.NDB_LANDING_COUPON,
        ]).catch((error) => console.log(error));
      },
      [ndbCoupon, deleteUserStorageItems]
    );

    useEffect(() => {
      if (!ndbCoupon || firedForCouponRef.current === ndbCoupon) return;
      firedForCouponRef.current = ndbCoupon;

      sendPromoNDBCodeAutofillEvent({ code: ndbCoupon });

      if (!formApiRef.current?.getValue('promocode')) {
        formApiRef.current?.setValue('promocode', ndbCoupon);
      }
    }, [ndbCoupon]);

    useEffect(() => {
      if (!(isMobileDevice && isErrorSupportClicked)) return;

      let timeoutId: ReturnType<typeof setTimeout>;

      const handler = () => {
        if (!formApiRef.current?.getError('promocode')) return;

        setIsErrorSupportClicked(false);

        timeoutId = setTimeout(() => {
          scrollToPromoCodeInput(ref, DISTANCE_TO_TOP);
        }, 0);
      };

      Intercom.onHide(handler);

      return () => {
        Intercom.offHide(handler);
        clearTimeout(timeoutId);
      };
    }, [isMobileDevice, isErrorSupportClicked]);

    return (
      <Form<IPromocodeForm>
        formApiRef={formApiRef}
        onSubmit={({ values }) => handleSubmit(values)}
      >
        <ScInputComponent>
          <Input
            addons={
              <div>
                <Button
                  autoPaddingHorizontal
                  disabled={isError || !isUserCanIssueBonuses}
                  isLoading={promocodePending}
                  paddingX={11}
                  secondary
                  type="submit"
                  xSmall
                >
                  {t('bonuses:activate')}
                </Button>
              </div>
            }
            autoComplete="off"
            background={blackColor}
            disabled={!isUserCanIssueBonuses}
            initialValue={ndbCoupon}
            mode={ThemeMode.Dark}
            name="promocode"
            onChange={handleChange}
            placeholder={t('bonuses:claimBonus.enterCode')}
            validate={required}
            validateOn="submit"
          />
        </ScInputComponent>
      </Form>
    );
  }
);
